import { all, put, takeEvery, select, race, take } from 'redux-saga/effects'
import { SUCCESS, FAILED } from 'crom-api-constants'
import _ from 'lodash'

import { getEntity, getAuthorization, getLocale, getCustomData } from '@global/selectors'
import config from '@config'

import { BLOCK_DATA_LOADED } from '../../../../../../components/Workspace/components/HocBlock/constants'
import { blockSaveCustomData, setIsScrolled } from '../../../../../../components/Workspace/components/HocBlock/actions'

import { CONFIRM_DIALOG_ON_BUTTON_CLICK } from '../../../../../../../../components/Dialog/components/Confirm/constants'

import { openDialog, dialogSetLoading, closeDialog } from '../../../../../../../../services/dialog/actions'
import { error } from '../../../../../../../../services/toastr'

import { LOCALE_PREFIX_SIGNALS } from '../Blocks/SyntheticIndicators/constants'

import {
    TOGGLE_SIGNAL_DETAIL,
    TOGGLE_SIGNAL_FLAG,
    SIGNALS_OPEN_CIA_NEWS,
    SIGNALS_CIA_NEWS_REQUEST,
    SIGNALS_SET_CIA_NEWS_BTN_DISABLED,
    SET_IS_SCROLLED,
} from './constants'
import { signalsCiaNewsRequest, signalsSetCiaNewsBtnDisabled } from './actions'

const SIGNALS_CIA_NEWS_REQUEST_SUCCESS = `${SIGNALS_CIA_NEWS_REQUEST}_${SUCCESS}`
const SIGNALS_CIA_NEWS_REQUEST_FAILED = `${SIGNALS_CIA_NEWS_REQUEST}_${FAILED}`

function* onDataLoaded(_action) {
    const blockKey = _.get(_action, 'data.blockKey', null)
    if (blockKey !== config.modules.report.blocks.syntheticIndicators.key) return null

    const module = _.get(_action, 'data.module', null)
    const id = _.get(_action, 'data.id', null)
    if (!module || !id || !blockKey) return null

    // Custom data for block
    yield put(
        blockSaveCustomData(module, id, blockKey, { toggledDetails: {}, toggledFlags: {}, disableCiaNewsBtn: false })
    )

    return null
}

function* syntheticIndicatorsChangeCustomData(action) {
    const blockKey = _.get(action, 'data.blockKey', null)
    if (blockKey !== config.modules.report.blocks.syntheticIndicators.key) return null

    const module = _.get(action, 'data.module', null)
    const id = _.get(action, 'data.id', null)
    if (!module || !id || !blockKey) return null

    const customData = yield select(getCustomData, module, blockKey, id)
    const incomingChange = _.get(action, 'data.customData', null)

    const newCustomData = { ...customData, ...incomingChange }

    yield put(blockSaveCustomData(module, id, blockKey, newCustomData))

    return null
}

function* ciaNewsCallSaga(action) {
    const module = action?.data?.module
    const blockKey = action?.data?.blockKey
    const id = action?.data?.id

    if (!module || !blockKey || !id) return null

    if (blockKey !== config.modules.report.blocks.syntheticIndicators.key) return null

    yield put(signalsSetCiaNewsBtnDisabled(module, id, blockKey, { disableCiaNewsBtn: true }))

    const authorizationData = yield select(getAuthorization)
    const entity = yield select(getEntity, module, id)
    const locale = yield select(getLocale)

    yield put(
        signalsCiaNewsRequest({
            id: authorizationData.get('user').get('userId'),
            ic: entity.get('corporation').get('regNbr'),
            country: entity.get('country').get('iso2'),
            lang: locale,
        })
    )

    const result = yield race({
        success: take(SIGNALS_CIA_NEWS_REQUEST_SUCCESS),
        failed: take(SIGNALS_CIA_NEWS_REQUEST_FAILED),
    })

    if (result.failed) {
        error({
            id: `${LOCALE_PREFIX_SIGNALS}.ciaNewsError`,
        })

        yield put(signalsSetCiaNewsBtnDisabled(module, id, blockKey, { disableCiaNewsBtn: false }))

        return null
    }

    // Dialog settings
    const dialogId = 'shareDataWithCia'
    const dialogSettings = {
        header: { textId: 'shareDataWithCiaTitle', icon: 'warning' },
        question: { textId: 'shareDataWithCiaText' },
        buttons: ['ciaYes', 'ciaNo'],
        manualClose: true,
    }

    const souhlas = result.success?.payload?.response?.body?.souhlas
    switch (souhlas) {
        case 0: {
            const userResponse = yield all({
                answer: race({
                    yes: take(CONFIRM_DIALOG_ON_BUTTON_CLICK('ciaYes', dialogId)),
                    cancel: take(CONFIRM_DIALOG_ON_BUTTON_CLICK('ciaNo', dialogId)),
                }),
                dialog: put(openDialog('confirm', dialogId, dialogSettings)),
            })

            if (!userResponse.answer.yes) {
                yield put(closeDialog('confirm', dialogId))
                break
            }

            yield put(dialogSetLoading('confirm', dialogId, true))

            yield put(
                signalsCiaNewsRequest({
                    id: authorizationData.get('user').get('userId'),
                    ic: entity.get('corporation').get('regNbr'),
                    country: entity.get('country').get('iso2'),
                    email: authorizationData.get('user').get('email'),
                    jmeno: authorizationData.get('user').get('firstName'),
                    prijmeni: authorizationData.get('user').get('lastName'),
                    lang: locale,
                    companyRegNbr: authorizationData.get('company').get('companyRegNbr'),
                    companyCountryIso2: authorizationData.get('company').get('companyCountryIso2'),
                })
            )

            const registerUser = yield race({
                success: take(SIGNALS_CIA_NEWS_REQUEST_SUCCESS),
                failed: take(SIGNALS_CIA_NEWS_REQUEST_FAILED),
            })

            yield put(dialogSetLoading('confirm', dialogId, false))

            const url = registerUser.success?.payload?.response?.body?.url
            if (registerUser.failed || !url) {
                error({
                    id: `${LOCALE_PREFIX_SIGNALS}.ciaNewsError`,
                })
                yield put(closeDialog('confirm', dialogId))
                break
            }

            yield put(closeDialog('confirm', dialogId))
            window.open(url, '_blank')
            break
        }
        case 1: {
            const ciaNewsUrl = result.success?.payload?.response?.body?.url

            if (!ciaNewsUrl) {
                error({
                    id: `${LOCALE_PREFIX_SIGNALS}.ciaNewsError`,
                })
                break
            }

            window.open(ciaNewsUrl, '_blank')
            break
        }
        default: {
            error({
                id: `${LOCALE_PREFIX_SIGNALS}.ciaNewsError`,
            })
            yield put(closeDialog('confirm', dialogId))
            break
        }
    }

    yield put(signalsSetCiaNewsBtnDisabled(module, id, blockKey, { disableCiaNewsBtn: false }))

    return null
}

function* setIsScrolledSaga(action) {
    yield put(setIsScrolled(action.data))
}

export default function* blockTransformSaga() {
    yield all([
        takeEvery(BLOCK_DATA_LOADED, onDataLoaded),
        takeEvery(TOGGLE_SIGNAL_DETAIL, syntheticIndicatorsChangeCustomData),
        takeEvery(TOGGLE_SIGNAL_FLAG, syntheticIndicatorsChangeCustomData),
        takeEvery(SIGNALS_SET_CIA_NEWS_BTN_DISABLED, syntheticIndicatorsChangeCustomData),
        takeEvery(SIGNALS_OPEN_CIA_NEWS, ciaNewsCallSaga),
        takeEvery(SET_IS_SCROLLED, setIsScrolledSaga),
    ])
}
